import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, Waves, Landmark, Mountain, UtensilsCrossed, Compass, Award, Users, PenLine, Phone } from "lucide-react";
import { useLanguage, pick } from "@/i18n";
import { api } from "@/api";
import ExperienceCard from "@/components/ExperienceCard";
import SectionHeader from "@/components/SectionHeader";
import MarqueeStrip from "@/components/MarqueeStrip";
import CampaniaMap from "@/components/CampaniaMap";
import PhotoCarousel from "@/components/PhotoCarousel";
import ContactForm from "@/components/ContactForm";
import { useSEO } from "@/hooks/useSEO";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: "easeOut" },
};

const IMGS = {
  hero: "https://images.unsplash.com/photo-1581416271259-abec87515f51?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxhbWFsZmklMjBjb2FzdCUyMGJvYXQlMjBzdW5zZXR8ZW58MHx8fHwxNzg3OTAwOTk0fDA&ixlib=rb-4.1.0&q=85",
  sea: "/images/ischia-panorama.jpeg",
  culture: "https://images.unsplash.com/photo-1686252183235-67dfafa22f60?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxwb21wZWlpJTIwYW5jaWVudCUyMHJ1aW5zfGVufDB8fHx8MTc4NzkwMDk5NHww&ixlib=rb-4.1.0&q=85",
  nature: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?q=85&w=1600&auto=format&fit=crop",
  taste: "https://images.unsplash.com/photo-1620475676913-9497df261cc3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxpdGFsaWFuJTIwZmluZSUyMGRpbmluZyUyMHdpbmV8ZW58MHx8fHwxNzg3OTAwOTk0fDA&ixlib=rb-4.1.0&q=85",
  boat: "https://images.unsplash.com/photo-1710897071793-db30046f5ed6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxhbWFsZmklMjBjb2FzdCUyMGJvYXQlMjBzdW5zZXR8ZW58MHx8fHwxNzg3OTAwOTk0fDA&ixlib=rb-4.1.0&q=85",
};

const HERO_SLIDES = [
  { src: "/images/pozzuoli-coast.jpeg", alt: "hero_alt_1" },
  { src: "/images/ischia-castello.jpeg", alt: "hero_alt_2" },
  { src: "/images/procida-corricella.jpeg", alt: "hero_alt_3" },
  { src: "/images/capri-faraglioni.jpeg", alt: "hero_alt_4" },
  { src: "/images/ischia-santangelo.jpeg", alt: "hero_alt_5" },
];

export default function Home() {
  const { lang, t } = useLanguage();
  const [featured, setFeatured] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [stories, setStories] = useState([]);
  const [slide, setSlide] = useState(0);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 110]);

  useSEO(t("seo_home_title"), t("seo_home_desc"));

  useEffect(() => {
    const id = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    api.experiences({ featured: true }).then(setFeatured).catch(() => {});
    api.destinations().then(setDestinations).catch(() => {});
    api.stories().then((s) => setStories(s.slice(0, 4))).catch(() => {});
  }, []);

  const dimensions = [
    { key: "sea", icon: Waves, img: IMGS.sea, to: "/experiences?category=sea", title: t("dim_sea"), desc: t("dim_sea_desc"), id: "dim-sea" },
    { key: "culture", icon: Landmark, img: IMGS.culture, to: "/experiences?category=culture", title: t("dim_culture"), desc: t("dim_culture_desc"), id: "dim-culture" },
    { key: "nature", icon: Mountain, img: IMGS.nature, to: "/experiences?category=nature", title: t("dim_nature"), desc: t("dim_nature_desc"), id: "dim-nature" },
    { key: "taste", icon: UtensilsCrossed, img: IMGS.taste, to: "/taste", title: t("dim_taste"), desc: t("dim_taste_desc"), id: "dim-taste" },
  ];

  const whyItems = [
    { icon: Compass, text: t("why_1") },
    { icon: Award, text: t("why_2") },
    { icon: Users, text: t("why_3") },
    { icon: PenLine, text: t("why_4") },
    { icon: Phone, text: t("why_5") },
  ];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-[92vh] min-h-[600px] flex items-end overflow-hidden bg-ink" data-testid="hero-section">
        <motion.div className="absolute inset-0" style={{ y: heroY }}>
          {HERO_SLIDES.map((s, i) => (
            <img
              key={s.src}
              src={s.src}
              alt={t(s.alt)}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1600ms] ease-out ${i === slide ? "opacity-100" : "opacity-0"}`}
              style={i === slide ? { transform: "scale(1.06)", transition: "opacity 1.6s ease-out, transform 6s ease-out" } : { transform: "scale(1)" }}
            />
          ))}
        </motion.div>
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute bottom-24 right-6 lg:right-10 z-10 flex flex-col gap-2" data-testid="hero-slide-dots">
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              data-testid={`hero-dot-${i}`}
              onClick={() => setSlide(i)}
              aria-label={`Slide ${i + 1}`}
              className={`w-1.5 transition-all duration-500 ${i === slide ? "h-8 bg-terracotta" : "h-3 bg-ivory/40 hover:bg-ivory/70"}`}
            />
          ))}
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-24 w-full">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-[11px] font-medium uppercase tracking-[0.3em] text-ivory/80 mb-6"
          >
            {t("tagline")}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.9 }}
            className="font-serif text-5xl md:text-7xl lg:text-8xl text-ivory leading-[1.0] tracking-tight max-w-4xl"
          >
            {t("hero_line1")}
            <br />
            <span className="italic font-light text-terracotta">{t("hero_line2")}</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="mt-6 max-w-xl text-base md:text-lg text-ivory/85 leading-relaxed"
            data-testid="hero-sub"
          >
            {t("hero_sub")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.8 }}
            className="flex flex-wrap gap-4 mt-10"
          >
            <Link
              to="/experiences"
              data-testid="hero-discover-button"
              className="inline-flex items-center gap-2 bg-ivory text-ink text-xs font-medium uppercase tracking-[0.2em] px-7 py-4 hover:bg-terracotta hover:text-ink transition-colors duration-300"
            >
              {t("hero_cta1")} <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
            <Link
              to="/create"
              data-testid="hero-create-button"
              className="inline-flex items-center gap-2 border border-ivory/60 text-ivory text-xs font-medium uppercase tracking-[0.2em] px-7 py-4 hover:bg-ivory hover:text-ink transition-colors duration-300"
            >
              {t("hero_cta2")}
            </Link>
          </motion.div>
        </div>
      </section>

      <PhotoCarousel />

      <MarqueeStrip />

      {/* COPY */}
      <section className="py-28 lg:py-36" data-testid="intro-copy-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp}>
            <p className="font-serif text-3xl md:text-4xl leading-snug text-ink">{t("copy_1")}</p>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-ink/60">{t("copy_2")}</p>
            <p className="mt-4 text-base md:text-lg leading-relaxed text-ink/60">{t("copy_3")}</p>
            <p className="mt-4 text-base md:text-lg leading-relaxed text-ink/60">{t("copy_4")}</p>
            <p className="mt-8 font-serif text-2xl italic text-medblue">{t("copy_5")}</p>
          </motion.div>
        </div>
      </section>

      {/* DIMENSIONS */}
      <section className="py-24 bg-sand" data-testid="dimensions-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp}>
            <SectionHeader overline="Flegrea Experience" title={t("dim_title")} />
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
            {dimensions.map((d, i) => (
              <motion.div key={d.key} {...fadeUp} transition={{ duration: 0.7, delay: i * 0.1 }}>
                <Link to={d.to} data-testid={d.id} className="group block img-zoom">
                  <div className="relative overflow-hidden aspect-[3/4] rounded-t-[45%]">
                    <img src={d.img} alt={d.title} loading="lazy" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/25 group-hover:bg-black/10 transition-colors duration-500" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <d.icon size={20} strokeWidth={1.25} className="text-ivory mb-3" />
                      <h3 className="font-serif text-3xl text-ivory tracking-wide">{d.title}</h3>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-ink/60 leading-relaxed">{d.desc}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* EXPLORE CAMPANIA */}
      <section className="py-28 lg:py-36 relative overflow-hidden" data-testid="explore-section">
        <span className="ghost-word absolute top-4 right-0 text-[9rem] lg:text-[13rem] hidden md:block" aria-hidden="true">CAMPANIA</span>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-5 gap-12 items-start">
          <motion.div {...fadeUp} className="lg:col-span-2">
            <SectionHeader overline="Campania" title={t("explore_title")} sub={t("explore_sub")} />
            <div className="mt-10 border-t border-stone2/50">
              {destinations.map((d) => (
                <Link
                  key={d.slug}
                  to={`/experiences?destination=${d.slug}`}
                  data-testid={`explore-dest-${d.slug}`}
                  className="group flex items-center justify-between py-4 border-b border-stone2/50"
                >
                  <div className="flex items-center gap-3">
                    <MapPin size={14} strokeWidth={1.5} className="text-terracotta" />
                    <span className="font-serif text-xl text-ink group-hover:text-medblue transition-colors duration-300">
                      {pick(d.name, lang)}
                    </span>
                  </div>
                  <ArrowRight size={16} strokeWidth={1.5} className="text-ink/30 transition-transform duration-300 group-hover:translate-x-1.5 group-hover:text-medblue" />
                </Link>
              ))}
            </div>
          </motion.div>
          <motion.div {...fadeUp} className="lg:col-span-3 lg:sticky lg:top-28">
            <CampaniaMap destinations={destinations} />
          </motion.div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="py-24 bg-sand" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp} className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeader overline={t("nav_experiences")} title={t("featured_title")} sub={t("featured_sub")} />
            <Link to="/experiences" data-testid="featured-all-link" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-medblue hover:text-ink transition-colors duration-300">
              {t("back_experiences")} <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-14 mt-16">
            {featured.slice(0, 8).map((exp) => (
              <ExperienceCard key={exp.slug} exp={exp} />
            ))}
          </div>
        </div>
      </section>

      {/* FLEGREA BY SEA */}
      <section className="relative py-32 lg:py-44 overflow-hidden" data-testid="bysea-section">
        <img src={IMGS.boat} alt="Flegrea by Sea" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-medblue/50" />
        <span className="ghost-word-dark absolute bottom-6 right-0 text-[10rem] lg:text-[16rem] hidden md:block" aria-hidden="true">MARE</span>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp}>
            <SectionHeader dark title={t("bysea_title")} sub={t("bysea_sub")} />
            <Link
              to="/boat"
              data-testid="bysea-cta"
              className="inline-flex items-center gap-2 mt-10 bg-ivory text-ink text-xs font-medium uppercase tracking-[0.2em] px-7 py-4 hover:bg-terracotta transition-colors duration-300"
            >
              {t("bysea_cta")} <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* TASTE */}
      <section className="py-28 lg:py-36" data-testid="taste-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <motion.div {...fadeUp} className="order-2 lg:order-1 overflow-hidden aspect-[4/3] img-zoom">
            <img src={IMGS.taste} alt="Taste Campania" loading="lazy" className="w-full h-full object-cover" />
          </motion.div>
          <motion.div {...fadeUp} className="order-1 lg:order-2">
            <SectionHeader overline="Food & Wine" title={t("taste_title")} sub={t("taste_sub")} />
            <p className="mt-6 text-sm leading-relaxed text-ink/60">{t("make_taste_sub")}</p>
            <Link
              to="/taste"
              data-testid="taste-cta"
              className="inline-flex items-center gap-2 mt-8 border border-ink text-ink text-xs font-medium uppercase tracking-[0.2em] px-7 py-4 hover:bg-ink hover:text-ivory transition-colors duration-300"
            >
              {t("taste_cta")} <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* BEYOND THE COAST */}
      <section className="py-28 lg:py-36 bg-sand relative overflow-hidden" data-testid="beyond-section">
        <span className="ghost-word absolute top-4 left-0 text-[9rem] lg:text-[13rem] hidden md:block" aria-hidden="true">WILD</span>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <motion.div {...fadeUp}>
            <SectionHeader overline={t("dim_nature")} title={t("beyond_title")} sub={t("beyond_sub")} />
            <p className="mt-6 font-serif text-xl italic text-ink/50">
              From Volcanoes to Mountains. From Ancient Cities to the Mediterranean Sea.
            </p>
            <Link
              to="/experiences?category=nature"
              data-testid="beyond-cta"
              className="inline-flex items-center gap-2 mt-8 border border-ink text-ink text-xs font-medium uppercase tracking-[0.2em] px-7 py-4 hover:bg-sage hover:border-sage hover:text-ivory transition-colors duration-300"
            >
              {t("beyond_cta")} <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </motion.div>
          <motion.div {...fadeUp} className="overflow-hidden rounded-t-[40%] aspect-[3/4] max-w-md ml-auto w-full img-zoom">
            <img src={IMGS.nature} alt="Beyond the Coast" loading="lazy" className="w-full h-full object-cover" />
          </motion.div>
        </div>
      </section>

      {/* CREATE YOUR EXPERIENCE */}
      <section className="py-28 lg:py-36 bg-medblue text-ivory" data-testid="create-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <motion.div {...fadeUp}>
            <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-ivory/60 mb-4">Bespoke</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">{t("create_title")}</h2>
            <p className="mt-5 text-ivory/70 text-base md:text-lg">{t("create_sub")}</p>
            <Link
              to="/create"
              data-testid="create-cta"
              className="inline-flex items-center gap-2 mt-10 bg-ivory text-medblue text-xs font-medium uppercase tracking-[0.2em] px-8 py-4 hover:bg-terracotta hover:text-ink transition-colors duration-300"
            >
              {t("create_cta")} <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* WHY */}
      <section className="py-24" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp}>
            <SectionHeader title={t("why_title")} />
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-stone2/50 mt-14 border border-stone2/50">
            {whyItems.map((w, i) => (
              <div key={i} data-testid={`why-item-${i}`} className="bg-ivory p-8 flex flex-col items-start gap-4">
                <w.icon size={22} strokeWidth={1.25} className="text-medblue" />
                <span className="text-sm font-medium text-ink leading-snug">{w.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STORIES */}
      <section className="py-24 lg:py-32 bg-sand" data-testid="stories-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp}>
            <SectionHeader overline="Companion" title={t("stories_title")} sub={t("stories_sub")} />
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12 mt-16">
            {stories.map((s, i) => (
              <motion.div key={s.slug} {...fadeUp} transition={{ duration: 0.7, delay: i * 0.08 }}>
                <Link to={`/companion/${s.slug}`} data-testid={`home-story-${s.slug}`} className="group block">
                  <div className="relative overflow-hidden aspect-[4/5] img-zoom">
                    <img src={s.image} alt={pick(s.title, lang)} loading="lazy" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/15 group-hover:bg-black/5 transition-colors duration-500" />
                  </div>
                  <div className="pt-5">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-terracotta mb-2">{s.minutes} min</p>
                    <h3 className="font-serif text-2xl leading-tight text-ink group-hover:text-medblue transition-colors duration-300">{pick(s.title, lang)}</h3>
                    <p className="mt-2 text-sm text-ink/55 leading-relaxed line-clamp-2">{pick(s.excerpt, lang)}</p>
                    <span className="mt-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-medblue">
                      {t("story_cta")} <ArrowRight size={13} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="py-24 bg-sand" data-testid="home-contact-section">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp}>
            <SectionHeader overline={t("need_help")} title={t("contact_title")} sub={t("contact_sub")} />
            <div className="mt-10">
              <ContactForm compact />
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ TEASER */}
      <section className="py-24" data-testid="home-faq-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp}>
            <SectionHeader title={t("faq_page_title")} />
            <div className="mt-10 space-y-6">
              {[1, 2, 3].map((n) => (
                <div key={n} className="border-b border-stone2/50 pb-6" data-testid={`home-faq-${n}`}>
                  <h3 className="font-serif text-xl text-ink">{t(`faq_q${n}`)}</h3>
                  <p className="mt-2 text-sm text-ink/60 leading-relaxed">{t(`faq_a${n}`)}</p>
                </div>
              ))}
            </div>
            <Link to="/faq" data-testid="home-faq-link" className="inline-flex items-center gap-2 mt-8 text-[11px] uppercase tracking-[0.2em] text-medblue hover:text-ink transition-colors duration-300">
              {t("all_faqs")} <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </motion.div>
        </div>
      </section>

      <MarqueeStrip dark={false} />

      {/* FINAL CTA */}
      <section className="py-28 lg:py-40" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight text-ink leading-[1.05]">{t("final_title")}</h2>
            <p className="mt-6 text-ink/60 text-base md:text-lg">{t("final_sub")}</p>
            <div className="flex flex-wrap justify-center gap-4 mt-10">
              <Link
                to="/experiences"
                data-testid="final-discover-button"
                className="inline-flex items-center gap-2 bg-medblue text-ivory text-xs font-medium uppercase tracking-[0.2em] px-8 py-4 hover:bg-ink transition-colors duration-300"
              >
                {t("hero_cta1")} <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
              <Link
                to="/create"
                data-testid="final-create-button"
                className="inline-flex items-center gap-2 border border-ink text-ink text-xs font-medium uppercase tracking-[0.2em] px-8 py-4 hover:bg-ink hover:text-ivory transition-colors duration-300"
              >
                {t("hero_cta2")}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
